// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { IApplicationInsights } from '@microsoft/applicationinsights-web';
import { SignedInState, SignInContext } from 'authentication/SignInProvider';
import { useContext } from 'react';
import { BotServerAppInsights } from 'utils/BotServerAppInsights';
import { User } from 'utils/User';

export const useSignIn = (): SignInContext => useContext(SignInContext);
export const useSignedInState = (): SignedInState => useContext(SignInContext).signedInState;
export const useAppInsights = (): IApplicationInsights | undefined => useContext(SignInContext)?.appInsights;
export const useBotServerAppInsights = (): BotServerAppInsights | undefined =>
  useContext(SignInContext)?.botServerAppInsights;
export const useIsSignedIn = (): boolean => useSignedInState() === 'SignedIn';
export const useUser = (): User | undefined => useContext(SignInContext).user;
export const useUserEmail = (): string | undefined => useUser()?.email;
export const useUserName = (): string | undefined => useUser()?.name;
export const useGraphToken = (): string | undefined => useUser()?.graphToken;
export const useFunctionsAppToken = (): string | undefined => useUser()?.functionsAppToken;
export const useUserAlias = (): string | undefined => useUserEmail()?.split('@')[0];
export const useUserInitials = (): string | undefined => {
  const name = useUserName();
  return name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : undefined;
};
